import { Loader, Tooltip } from '@mantine/core';
import { IconCircleCheckFilled, IconCircleXFilled } from '@tabler/icons-react';
import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';
import GeneratingIndicator from './GeneratingIndicator';

type StatusKey = BackgroundProcess['status'];

type Props = {
  status: StatusKey;
  tooltipLabel?: string;
  processingTooltipLabel?: string;
  withProcessingBackground?: boolean;
};

export default function FileStatusIcon({
  status,
  tooltipLabel,
  processingTooltipLabel,
  withProcessingBackground = false,
}: Props) {
  return (
    <Tooltip
      label={resolveLabel(status, tooltipLabel, processingTooltipLabel)}
      multiline
      maw={400}
      withArrow
    >
      <span
        style={{ display: 'inline-flex', alignItems: 'center' }}
        tabIndex={0}
        data-file-status={status}
      >
        <StatusIcon status={status} withProcessingBackground={withProcessingBackground} />
      </span>
    </Tooltip>
  );
}

const defaultLabels: Record<StatusKey, string> = {
  PENDING: 'Extracting data from document...',
  PROCESSING: 'Extracting data from document...',
  SUCCESS: 'Data extracted - document is ready',
  FAILED: 'Processing failed',
};

function StatusIcon({
  status,
  withProcessingBackground,
}: {
  status: StatusKey;
  withProcessingBackground: boolean;
}) {
  switch (status) {
    case 'PENDING':
    case 'PROCESSING':
      return withProcessingBackground ? <GeneratingIndicator /> : <Loader size={16} />;
    case 'SUCCESS':
      return <IconCircleCheckFilled size={16} color="var(--mantine-color-green-5)" />;
    case 'FAILED':
      return <IconCircleXFilled size={16} color="var(--mantine-color-red-6)" />;
  }
}

function resolveLabel(status: StatusKey, tooltipLabel?: string, processingTooltipLabel?: string) {
  if (tooltipLabel) return tooltipLabel;
  if (processingTooltipLabel && (status === 'PENDING' || status === 'PROCESSING')) {
    return processingTooltipLabel;
  }
  return defaultLabels[status];
}
